import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/common';

const LoginPage: React.FC = () => {
  const { login, signup, signInWithGoogle, initError } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isSignUp) {
        await signup(email, password);
      } else {
        await login(email, password);
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  if (initError) {
    return (
      <div className="min-h-screen bg-kids-bg dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">{'\u{1F622}'}</div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Oops!</h1>
          <p className="text-gray-600 dark:text-gray-300">Something went wrong. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kids-bg dark:bg-slate-900 flex items-center justify-center p-4 relative">
      {/* Back to Home Link */}
      <Link
        to="/"
        className="absolute top-4 left-4 flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-kids-primary dark:hover:text-indigo-400 transition-colors"
      >
        <span className="text-2xl">{'\u2190'}</span>
        <span className="font-bold">Back to Home</span>
      </Link>

      <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-md w-full shadow-xl dark:shadow-slate-900">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4 animate-bounce-slow">{'\u{1F433}'}</div>
          <h1 className="text-3xl font-bold text-kids-primary dark:text-indigo-400">Litrlly Kids</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">Learn English the fun way!</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 dark:text-gray-200 font-bold mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-white focus:border-kids-primary dark:focus:border-indigo-400 focus:outline-none text-lg"
              placeholder="your@email.com"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-200 font-bold mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-white focus:border-kids-primary dark:focus:border-indigo-400 focus:outline-none text-lg"
              placeholder="Enter password"
              required
              minLength={6}
            />
          </div>

          {error && (
            <div className="bg-red-100 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-xl p-3 text-red-700 dark:text-red-400 text-center">
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Please wait...' : isSignUp ? 'Create Account' : 'Log In'}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center my-6">
          <div className="flex-1 border-t-2 border-gray-200 dark:border-slate-600"></div>
          <span className="px-4 text-gray-500 dark:text-gray-400">or</span>
          <div className="flex-1 border-t-2 border-gray-200 dark:border-slate-600"></div>
        </div>

        {/* Google Sign In */}
        <Button
          onClick={handleGoogleSignIn}
          variant="secondary"
          size="large"
          fullWidth
          disabled={loading}
        >
          <span className="text-2xl">G</span>
          Continue with Google
        </Button>

        {/* Toggle Sign Up/Login */}
        <div className="text-center mt-6">
          <button
            type="button"
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-kids-primary dark:text-indigo-400 font-bold hover:underline"
          >
            {isSignUp ? 'Already have an account? Log in' : "Don't have an account? Sign up"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
